/**
 * ONE collection, opened up.
 *
 * Evan (2026-08-24): "the search page in admin dash - i need to be able to expand view of a
 * collection and really get in the weeds of editing it as I want."
 *
 * The Collections table answers "what exists and what is live". It cannot answer the question
 * underneath that one — WHAT IS ACTUALLY IN THIS COLLECTION, AND WHY — and every complaint about
 * collections so far has really been that question. Four tabs, in the order the question is asked:
 *
 *   Venues   every venue the rule returns, in order, with score / raters / city / photo and THE
 *            NUMBER THE RULE LOOKED AT. Pin to the top, or exclude one, without touching the rule.
 *   Rules    title, subtitle, city scope, sort order, tag, categories, the neighbourhood box —
 *            with the row count RE-COMPUTED AS YOU TYPE, before anything is saved.
 *   Cities   the 28 previewable cities, and how many venues this collection fills in each.
 *   Cover    the existing picker, unchanged.
 *
 * ─── ⚠️ WHY "CITIES" IS THE TAB THAT MATTERS ────────────────────────────────────────────────────
 * Evan's recurring failure is a collection that only fills in New Orleans. Measured on production
 * 2026-08-24 across the 28 cities with a previewable pool, counting where a LIVE collection clears
 * the app's 4-venue minimum:
 *     Best patios              0 of 28      Where people are dancing   1 of 28
 *     {city} classics          4            It's the room              4
 *     Still going at 2am       4            Trending this week         4
 *     Worth it for the lineup  4            Go for the drinks          5
 *     The music is right       5            A night out in {city}      8
 *     Just rated in {city}     8            Underground               21
 * "Best patios" is switched ON and renders for nobody, anywhere. The table says "live" and is not
 * wrong; it is just not the number he needs.
 *
 * ─── ⚠️ ONE COLLECTION IS VIEWER-DEPENDENT ──────────────────────────────────────────────────────
 * "Popular with friends" (`kind = 'friends'`) resolves through `auth.uid()` inside
 * `search_guide_venue_ids`. From this dashboard it returns the ADMIN's mutual follows or nothing at
 * all. Every tab here says so in place rather than rendering a zero, an empty list, or a coverage
 * map of 28 blanks — absent is not empty and not off.
 *
 * ─── DEGRADES, DOES NOT ERROR ───────────────────────────────────────────────────────────────────
 * `supabase/migrations/20260878000000_collection_detail_editing.sql` is WRITTEN AND UNAPPLIED. What
 * works before it lands, and what does not:
 *
 *   WORKS TODAY   the ordered venue list with photo / score / rater count (falls back to
 *                 `admin_search_guide_cover_options`, which is the same `search_guide_venue_ids`
 *                 under a different name); title; subtitle; city scope; sort order; tag;
 *                 categories; live-off; cover.
 *   NEEDS IT      the "why" column and the venue's city; pin; exclude; the live count for UNSAVED
 *                 rules; the per-city coverage tab; the neighbourhood box; and editing the three
 *                 `underground` / `neighborhood` rows at all — `admin_upsert_search_guide`'s kind
 *                 whitelist rejects them today, so renaming "Uptown" currently fails outright.
 *
 * Each of those says so in a quiet set-up note, never a red banner — the `CityOverviews` pattern.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useToast } from '../lib/useToast';
import { useEscToClose } from '../lib/useEscToClose';
import { ErrorBanner } from '../ui/ErrorBanner';
import { EmptyState } from '../ui/EmptyState';
import { StatCard } from '../ui/StatCard';
import { SkeletonRows } from '../ui/Skeleton';
import { Field } from '../ui/Field';
import { CollectionCoverPickerModal } from './CollectionCoverPickerModal';
import {
  CRITERION_TAGS,
  KIND_LABELS,
  KIND_RULES,
  MIN_VENUES,
  RULELESS_KINDS,
  VIEWER_KINDS,
  errText,
  isCityScoped,
  rpcMissing,
  type CoverOption,
  type GuideRow,
} from '../lib/collectionKinds';

const MIGRATION = 'supabase/migrations/20260878000000_collection_detail_editing.sql';

const SETUP_PREFIX = 'Not set up yet — apply ' + MIGRATION + ', then reload. ';

type Tab = 'venues' | 'rules' | 'cities' | 'cover';

/** One row of `admin_guide_venue_rows`. */
type GuideVenueRow = {
  /** Where the RULE puts it, before pins and excludes. */
  position: number;
  /** Where the READER sees it, after pins and excludes. Null = excluded, so nowhere. */
  effective_position: number | null;
  venue_id: string;
  name: string | null;
  city_id: string | null;
  city_name: string | null;
  photo_url: string | null;
  score: number | null;
  rater_count: number;
  fsq_category: string | null;
  fsq_rating: number | null;
  tags: string[];
  pinned: boolean;
  excluded: boolean;
  override_city_id: string | null;
  override_note: string | null;
  pin_rank: number | null;
  /** The rule's own number for this venue. Null for kinds with no per-venue evidence. */
  why: string | null;
};

type CoverageCity = {
  city_id: string | null;
  city_name: string;
  pool?: number;
  n: number;
  renders: boolean;
};

type Coverage = {
  scope: 'per_city' | 'global' | 'pinned_city' | 'viewer';
  min_venues: number;
  pinned_city_name?: string | null;
  cities: CoverageCity[];
};

type DryRun = { count: number; renders: boolean; sample: unknown[]; error: string | null };

function num(v: unknown): number | null {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function SetupNote({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-md border border-border/90 bg-white/[0.025] px-3 py-2 text-[11px] leading-relaxed text-muted">
      {children}
    </div>
  );
}

/** The amber "this cannot be measured from a dashboard" line. Never a zero. */
function ViewerDependentNote() {
  return (
    <div className="rounded-md border border-amber-500/30 bg-amber-500/[0.06] px-3 py-2 text-[11px] leading-relaxed text-amber-300">
      This collection reads <span className="font-semibold">each reader’s own mutual follows</span>{' '}
      (<code>auth.uid()</code>, inside <code>search_guide_venue_ids</code>). Run from here it returns
      your account’s friends, or nothing at all — so an empty list below would not mean the
      collection is empty, and a count would not be anybody’s count. It cannot be previewed from a
      dashboard at all; check it on a device signed in as a real user.
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * VENUES — the list, the evidence, and the two editorial overrides
 * ════════════════════════════════════════════════════════════════════════════════════════════ */

function VenuesTab({
  guide,
  scopeCityId,
  scopeCityName,
  canEdit,
  onDirty,
}: {
  guide: GuideRow;
  scopeCityId: string;
  scopeCityName: string | null;
  canEdit: boolean;
  onDirty: () => void;
}) {
  const [rows, setRows] = useState<GuideVenueRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [setupNote, setSetupNote] = useState<string | null>(null);
  const [overridesSupported, setOverridesSupported] = useState(true);
  const [busyVenue, setBusyVenue] = useState<string | null>(null);
  const [showExcluded, setShowExcluded] = useState(true);
  const { showError, showSuccess, toastNode } = useToast();

  const guideId = guide.id;
  const viewerScoped = VIEWER_KINDS.has(guide.kind);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const { data, error: rpcErr } = await supabase.rpc('admin_guide_venue_rows', {
      p_guide_id: guideId,
      p_city_id: scopeCityId || null,
      p_limit: 200,
    });
    if (!rpcErr) {
      const payload = (data ?? {}) as { rows?: GuideVenueRow[] };
      setRows(Array.isArray(payload.rows) ? payload.rows : []);
      setOverridesSupported(true);
      setSetupNote(null);
      setLoading(false);
      return;
    }
    if (!rpcMissing(rpcErr.message, 'admin_guide_venue_rows')) {
      setError(rpcErr.message);
      setRows([]);
      setLoading(false);
      return;
    }
    /*
     * Pre-migration fallback. `admin_search_guide_cover_options` is backed by the SAME
     * `search_guide_venue_ids`, so the ORDER AND MEMBERSHIP here are exact — what is missing is
     * only the evidence column, the venue's city, and the ability to pin or exclude.
     */
    const fb = await supabase.rpc('admin_search_guide_cover_options', {
      p_guide_id: guideId,
      p_city_id: scopeCityId || null,
      p_limit: 200,
    });
    if (fb.error) {
      setError(fb.error.message);
      setRows([]);
    } else {
      const opts = Array.isArray(fb.data) ? (fb.data as CoverOption[]) : [];
      setRows(
        opts.map((o) => ({
          position: o.position,
          effective_position: o.position,
          venue_id: o.venue_id,
          name: o.name,
          city_id: null,
          city_name: null,
          photo_url: o.photo_url,
          score: o.score,
          rater_count: o.rater_count,
          fsq_category: null,
          fsq_rating: null,
          tags: [],
          pinned: false,
          excluded: false,
          override_city_id: null,
          override_note: null,
          pin_rank: null,
          why: null,
        })),
      );
      setOverridesSupported(false);
      setSetupNote(
        SETUP_PREFIX +
          'This list is exact — it comes from the same search_guide_venue_ids the rail uses — but ' +
          'the "why" column, each venue’s city, and pin / exclude need that migration.',
      );
    }
    setLoading(false);
  }, [guideId, scopeCityId]);

  useEffect(() => {
    void load();
  }, [load]);

  const setOverride = useCallback(
    async (venueId: string, mode: 'pin' | 'exclude') => {
      setBusyVenue(venueId);
      try {
        const { error: rpcErr } = await supabase.rpc('admin_set_guide_venue_override', {
          p_guide_id: guideId,
          p_venue_id: venueId,
          p_mode: mode,
          /* NULL = every city. A city-scoped collection is one row rendering 28 different lists,
             and "take this out" almost always means "take it out everywhere". */
          p_city_id: null,
          p_rank: null,
          p_note: null,
        });
        if (rpcErr) {
          throw new Error(
            rpcMissing(rpcErr.message, 'admin_set_guide_venue_override')
              ? SETUP_PREFIX + 'Pin and exclude need a table that does not exist yet.'
              : rpcErr.message,
          );
        }
        showSuccess(mode === 'pin' ? 'Pinned to the top' : 'Excluded from this collection');
        onDirty();
        await load();
      } catch (e: unknown) {
        showError(errText(e, 'Could not save that'));
      } finally {
        setBusyVenue(null);
      }
    },
    [guideId, load, onDirty, showError, showSuccess],
  );

  const clearOverride = useCallback(
    async (venueId: string) => {
      setBusyVenue(venueId);
      try {
        const { error: rpcErr } = await supabase.rpc('admin_clear_guide_venue_override', {
          p_guide_id: guideId,
          p_venue_id: venueId,
          p_city_id: null,
        });
        if (rpcErr) throw new Error(rpcErr.message);
        showSuccess('Back to the rule’s own answer');
        onDirty();
        await load();
      } catch (e: unknown) {
        showError(errText(e, 'Could not clear that'));
      } finally {
        setBusyVenue(null);
      }
    },
    [guideId, load, onDirty, showError, showSuccess],
  );

  const pinned = useMemo(
    () => rows.filter((r) => r.pinned && !r.excluded).sort((a, b) => (a.pin_rank ?? 0) - (b.pin_rank ?? 0)),
    [rows],
  );

  /** Move one pin up or down inside the pinned block and write the whole new order. */
  const movePin = useCallback(
    async (venueId: string, delta: -1 | 1) => {
      const order = pinned.map((p) => p.venue_id);
      const i = order.indexOf(venueId);
      const j = i + delta;
      if (i < 0 || j < 0 || j >= order.length) return;
      [order[i], order[j]] = [order[j], order[i]];
      setBusyVenue(venueId);
      try {
        const { error: rpcErr } = await supabase.rpc('admin_reorder_guide_pins', {
          p_guide_id: guideId,
          p_venue_ids: order,
          p_city_id: null,
        });
        if (rpcErr) throw new Error(rpcErr.message);
        onDirty();
        await load();
      } catch (e: unknown) {
        showError(errText(e, 'Could not reorder'));
      } finally {
        setBusyVenue(null);
      }
    },
    [guideId, load, onDirty, pinned, showError],
  );

  const visible = useMemo(
    () => (showExcluded ? rows : rows.filter((r) => !r.excluded)),
    [rows, showExcluded],
  );

  const excludedCount = useMemo(() => rows.filter((r) => r.excluded).length, [rows]);
  const effectiveCount = useMemo(() => rows.filter((r) => !r.excluded).length, [rows]);

  if (viewerScoped) {
    return (
      <div className="space-y-4">
        {toastNode}
        <ViewerDependentNote />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {toastNode}
      {error ? <ErrorBanner>{error}</ErrorBanner> : null}
      {setupNote && !error ? <SetupNote>{setupNote}</SetupNote> : null}

      <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}>
        <StatCard
          label="The reader gets"
          value={loading ? null : effectiveCount}
          sub={
            effectiveCount >= MIN_VENUES
              ? `Clears the ${MIN_VENUES}-venue minimum in ${scopeCityName ?? 'this city'}`
              : `Under the ${MIN_VENUES} the app needs — dropped, not shown short`
          }
        />
        <StatCard label="Pinned" value={loading ? null : pinned.length} sub="Lifted above the rule’s order" />
        <StatCard label="Excluded" value={loading ? null : excludedCount} sub="Removed without changing the rule" />
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2 text-[11px] text-muted">
          <input
            type="checkbox"
            checked={showExcluded}
            onChange={(e) => setShowExcluded(e.target.checked)}
            className="h-3.5 w-3.5 accent-current"
          />
          Show excluded venues
        </label>
        <button type="button" onClick={() => void load()} disabled={loading} className="admin-btn admin-btn-secondary admin-btn-sm ml-auto">
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      {loading ? (
        <SkeletonRows rows={8} />
      ) : visible.length === 0 ? (
        <EmptyState
          title="This collection is empty here"
          hint={
            isCityScoped(guide.kind, guide.city_id)
              ? `The rule finds nothing in ${scopeCityName ?? 'this city'}. Try another city at the top, or check the Cities tab for where it does fill.`
              : 'The rule matches no venue anywhere right now.'
          }
        />
      ) : (
        <div className="overflow-x-auto rounded-xl border border-border">
          <table className="admin-table">
            <thead>
              <tr>
                <th className="px-3 py-3 text-left">#</th>
                <th className="px-3 py-3 text-left">Venue</th>
                <th className="px-3 py-3 text-right">Score</th>
                <th className="px-3 py-3 text-right">Raters</th>
                <th className="px-3 py-3 text-left">Why it qualified</th>
                {canEdit ? <th className="px-3 py-3 text-left">Edit</th> : null}
              </tr>
            </thead>
            <tbody>
              {visible.map((r, i) => (
                <tr
                  key={r.venue_id}
                  className={`border-b border-border/50 ${i % 2 === 0 ? 'bg-surface' : 'bg-rowAlt'} ${
                    r.excluded ? 'opacity-45' : ''
                  }`}
                >
                  <td className="px-3 py-3 align-top text-sm tabular-nums text-mutedDim">
                    {r.excluded ? '—' : (r.effective_position ?? r.position)}
                    {r.pinned && !r.excluded ? (
                      <div className="text-[10px] font-medium text-accent" title="Pinned above the rule's order">
                        pinned
                      </div>
                    ) : null}
                  </td>

                  <td className="px-3 py-3 align-top">
                    <div className="flex items-start gap-3">
                      <div className="h-11 w-11 shrink-0 overflow-hidden rounded-md border border-border bg-black">
                        {r.photo_url ? (
                          <img src={r.photo_url} alt="" referrerPolicy="no-referrer" loading="lazy" className="h-full w-full object-cover" />
                        ) : null}
                      </div>
                      <div className="min-w-0">
                        <div className={`admin-label-primary truncate ${r.excluded ? 'line-through' : ''}`}>
                          {r.name?.trim() || 'Untitled venue'}
                        </div>
                        <div className="text-[11px] text-mutedDim">
                          {r.city_name ?? '—'}
                          {r.fsq_category ? ` · ${r.fsq_category}` : ''}
                        </div>
                        {r.tags.length > 0 ? (
                          <div className="mt-1 truncate text-[10px] text-mutedDim">{r.tags.slice(0, 5).join(' · ')}</div>
                        ) : null}
                        {r.override_note ? (
                          <div className="mt-1 text-[10px] italic text-mutedDim">{r.override_note}</div>
                        ) : null}
                      </div>
                    </div>
                  </td>

                  <td className="px-3 py-3 align-top text-right admin-metric tabular-nums">{r.score ?? '—'}</td>
                  {/* Repo rule: a DISPLAYED count is distinct raters (venue_shado_unique_users). */}
                  <td className="px-3 py-3 align-top text-right text-sm tabular-nums text-muted">{r.rater_count}</td>

                  <td className="px-3 py-3 align-top text-[11px] leading-relaxed text-mutedDim">
                    {r.excluded ? (
                      <span className="text-amber-400">
                        Excluded by hand{r.override_city_id ? ' in this city' : ' everywhere'} — the rule still
                        matches it.
                      </span>
                    ) : (
                      r.why ?? '—'
                    )}
                  </td>

                  {canEdit ? (
                    <td className="px-3 py-3 align-top">
                      {!overridesSupported ? (
                        <span className="text-[10px] text-mutedDim">needs the migration</span>
                      ) : r.excluded ? (
                        <button
                          type="button"
                          disabled={busyVenue === r.venue_id}
                          onClick={() => void clearOverride(r.venue_id)}
                          className="admin-btn admin-btn-secondary admin-btn-sm"
                        >
                          Restore
                        </button>
                      ) : (
                        <div className="flex flex-wrap items-center gap-1.5">
                          {r.pinned ? (
                            <>
                              <button
                                type="button"
                                aria-label={`Move ${r.name ?? 'venue'} up`}
                                disabled={busyVenue === r.venue_id || pinned[0]?.venue_id === r.venue_id}
                                onClick={() => void movePin(r.venue_id, -1)}
                                className="admin-btn admin-btn-ghost admin-btn-sm !px-2"
                              >
                                ↑
                              </button>
                              <button
                                type="button"
                                aria-label={`Move ${r.name ?? 'venue'} down`}
                                disabled={
                                  busyVenue === r.venue_id ||
                                  pinned[pinned.length - 1]?.venue_id === r.venue_id
                                }
                                onClick={() => void movePin(r.venue_id, 1)}
                                className="admin-btn admin-btn-ghost admin-btn-sm !px-2"
                              >
                                ↓
                              </button>
                              <button
                                type="button"
                                disabled={busyVenue === r.venue_id}
                                onClick={() => void clearOverride(r.venue_id)}
                                className="admin-btn admin-btn-ghost admin-btn-sm"
                              >
                                Unpin
                              </button>
                            </>
                          ) : (
                            <button
                              type="button"
                              disabled={busyVenue === r.venue_id}
                              onClick={() => void setOverride(r.venue_id, 'pin')}
                              className="admin-btn admin-btn-secondary admin-btn-sm"
                            >
                              Pin to top
                            </button>
                          )}
                          <button
                            type="button"
                            disabled={busyVenue === r.venue_id}
                            onClick={() => void setOverride(r.venue_id, 'exclude')}
                            className="admin-btn admin-btn-ghost admin-btn-sm"
                            title="Remove this venue from this collection without changing the rule"
                          >
                            Exclude
                          </button>
                        </div>
                      )}
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * RULES — edited with the row count re-computed as you type, before anything is saved
 * ════════════════════════════════════════════════════════════════════════════════════════════ */

function RulesTab({
  guide,
  scopeCityId,
  cities,
  canEdit,
  onSaved,
}: {
  guide: GuideRow;
  scopeCityId: string;
  cities: { id: string; name: string }[];
  canEdit: boolean;
  onSaved: (patch: Partial<GuideRow>) => void;
}) {
  const [title, setTitle] = useState(guide.title);
  const [subtitle, setSubtitle] = useState(guide.subtitle ?? '');
  const [cityId, setCityId] = useState(guide.city_id ?? '');
  const [sortOrder, setSortOrder] = useState(String(guide.sort_order));
  const [tag, setTag] = useState(guide.tag ?? '');
  const [categories, setCategories] = useState((guide.categories ?? []).join(', '));
  const [box, setBox] = useState({ minLat: '', maxLat: '', minLng: '', maxLng: '' });
  const [dry, setDry] = useState<DryRun | null>(null);
  const [dryBusy, setDryBusy] = useState(false);
  const [dryUnsupported, setDryUnsupported] = useState(false);
  const [saving, setSaving] = useState(false);
  const { showError, showSuccess, toastNode } = useToast();

  const kind = guide.kind;
  const ruleless = RULELESS_KINDS.has(kind);
  const viewerScoped = VIEWER_KINDS.has(kind);

  const categoryArray = useMemo(
    () => categories.split(',').map((c) => c.trim()).filter(Boolean),
    [categories],
  );

  /**
   * The dry run. Debounced, and keyed on a request id so a slow early response cannot overwrite a
   * fast later one — the count is the thing being trusted, so a stale one is worse than none.
   */
  const seq = useRef(0);
  useEffect(() => {
    if (viewerScoped) return;
    const mine = ++seq.current;
    const t = window.setTimeout(() => {
      setDryBusy(true);
      void (async () => {
        const { data, error } = await supabase.rpc('admin_preview_search_guide_rules', {
          p_kind: kind,
          p_city_id: cityId || null,
          p_tag: tag || null,
          p_categories: categoryArray.length > 0 ? categoryArray : null,
          p_venue_ids: guide.venue_ids ?? null,
          p_scope_city_id: scopeCityId || null,
          p_area_min_lat: num(box.minLat),
          p_area_max_lat: num(box.maxLat),
          p_area_min_lng: num(box.minLng),
          p_area_max_lng: num(box.maxLng),
        });
        if (mine !== seq.current) return;
        if (error) {
          if (rpcMissing(error.message, 'admin_preview_search_guide_rules')) setDryUnsupported(true);
          setDry(null);
        } else {
          setDryUnsupported(false);
          setDry(data as DryRun);
        }
        setDryBusy(false);
      })();
    }, 450);
    return () => window.clearTimeout(t);
  }, [box, categoryArray, cityId, guide.venue_ids, kind, scopeCityId, tag, viewerScoped]);

  const save = useCallback(async () => {
    const t = title.trim();
    if (!t) {
      showError('A collection needs a title');
      return;
    }
    setSaving(true);
    try {
      const { error: rpcErr } = await supabase.rpc('admin_upsert_search_guide', {
        p_id: guide.id,
        p_title: t,
        p_subtitle: subtitle.trim() || null,
        p_kind: kind,
        p_city_id: cityId || null,
        p_tag: tag.trim() || null,
        p_categories: categoryArray.length > 0 ? categoryArray : null,
        p_venue_ids: guide.venue_ids,
        p_sort_order: Number(sortOrder) || guide.sort_order,
        p_is_active: guide.is_active,
      });
      if (rpcErr) {
        /* The live validator's whitelist stops at 'friends'. `underground` and `neighborhood` are
           live in the rule function and live on three rows, so those three cannot be edited until
           §8 of the migration lands. Say that instead of echoing "unknown kind underground". */
        throw new Error(
          rpcErr.message.toLowerCase().includes('unknown kind')
            ? `The database rejects "${kind}" — its validator predates this kind. ` + SETUP_PREFIX
            : rpcErr.message,
        );
      }

      if (kind === 'neighborhood' && (box.minLat || box.maxLat || box.minLng || box.maxLng)) {
        const area = await supabase.rpc('admin_set_search_guide_area', {
          p_id: guide.id,
          p_min_lat: num(box.minLat),
          p_max_lat: num(box.maxLat),
          p_min_lng: num(box.minLng),
          p_max_lng: num(box.maxLng),
        });
        if (area.error) {
          throw new Error(
            rpcMissing(area.error.message, 'admin_set_search_guide_area')
              ? 'Title and scope saved. The bounding box did not: ' + SETUP_PREFIX
              : area.error.message,
          );
        }
      }

      onSaved({
        title: t,
        subtitle: subtitle.trim() || null,
        city_id: cityId || null,
        tag: tag.trim() || null,
        categories: categoryArray.length > 0 ? categoryArray : null,
        sort_order: Number(sortOrder) || guide.sort_order,
      });
      showSuccess('Saved');
    } catch (e: unknown) {
      showError(errText(e, 'Could not save'));
    } finally {
      setSaving(false);
    }
  }, [
    box,
    categoryArray,
    cityId,
    guide.id,
    guide.is_active,
    guide.sort_order,
    guide.venue_ids,
    kind,
    onSaved,
    showError,
    showSuccess,
    sortOrder,
    subtitle,
    tag,
    title,
  ]);

  return (
    <div className="space-y-5">
      {toastNode}

      <SetupNote>
        <span className="text-textPrimary">{KIND_LABELS[kind] ?? kind}</span> — {KIND_RULES[kind] ?? 'No description for this kind yet.'}
        {ruleless ? ' There is nothing to tune here beyond the title, the scope and where it sits on the page.' : ''}
      </SetupNote>

      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Title" hint="{city} is replaced with the reader’s city.">
          <input value={title} onChange={(e) => setTitle(e.target.value)} className="admin-input" disabled={!canEdit} />
        </Field>
        <Field label="Subtitle" hint="Optional. Shown under the title on the card.">
          <input value={subtitle} onChange={(e) => setSubtitle(e.target.value)} className="admin-input" disabled={!canEdit} />
        </Field>

        <Field
          label="City scope"
          hint={
            cityId
              ? 'Fixed to one city: every reader everywhere sees this city’s venues.'
              : 'Follows the reader — one row that renders a different list in each of the 28 previewable cities.'
          }
        >
          <select value={cityId} onChange={(e) => setCityId(e.target.value)} className="admin-input" disabled={!canEdit}>
            <option value="">Follows the reader’s city</option>
            {cities.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Position on the page" hint="Lower sorts higher. Ties break on title.">
          <input
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value.replace(/[^0-9]/g, ''))}
            inputMode="numeric"
            className="admin-input"
            disabled={!canEdit}
          />
        </Field>

        {kind === 'criterion' ? (
          <Field label="Criterion" hint="Only these five are recorded on a log; the others are NULL on every row.">
            <select value={tag} onChange={(e) => setTag(e.target.value)} className="admin-input" disabled={!canEdit}>
              <option value="">—</option>
              {CRITERION_TAGS.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </Field>
        ) : null}

        {kind === 'tag' ? (
          <Field label="Thought tag" hint="Only 190 of 742 rated venues carry any thought tag, so tag collections fill slowly.">
            <input value={tag} onChange={(e) => setTag(e.target.value)} className="admin-input" disabled={!canEdit} />
          </Field>
        ) : null}

        {kind === 'category' ? (
          <Field
            label="Foursquare categories"
            hint="Comma-separated, matched exactly against venues.fsq_category."
            className="sm:col-span-2"
          >
            <input value={categories} onChange={(e) => setCategories(e.target.value)} className="admin-input" disabled={!canEdit} />
          </Field>
        ) : null}
      </div>

      {kind === 'neighborhood' ? (
        <section className="space-y-3">
          <h3 className="admin-section-label">Bounding box</h3>
          <SetupNote>
            There is no neighbourhood column on <code>venues</code> — <code>locality</code> is the
            municipality, and Foursquare’s neighbourhood attribute was never ingested. A lat/lng
            rectangle is the whole mechanism. New Orleans is a river crescent, so no axis-aligned
            box follows it: the seeded Downtown box reaches across the Mississippi into Algiers
            Point. Treat a box as a seed to be replaced, not a system. All four corners or none —
            a half-set box makes the rule fail closed and the rail silently disappears.
          </SetupNote>
          <div className="grid gap-3 sm:grid-cols-4">
            {(
              [
                ['minLat', 'South (min lat)'],
                ['maxLat', 'North (max lat)'],
                ['minLng', 'West (min lng)'],
                ['maxLng', 'East (max lng)'],
              ] as const
            ).map(([key, label]) => (
              <Field key={key} label={label}>
                <input
                  value={box[key]}
                  onChange={(e) => setBox((b) => ({ ...b, [key]: e.target.value }))}
                  inputMode="decimal"
                  placeholder="unchanged"
                  className="admin-input"
                  disabled={!canEdit}
                />
              </Field>
            ))}
          </div>
        </section>
      ) : null}

      {/* ── The count, before saving ─────────────────────────────────────────────────────────── */}
      <section className="rounded-xl border border-border bg-surface px-4 py-4">
        <h3 className="admin-section-label mb-2">If you saved this</h3>
        {viewerScoped ? (
          <ViewerDependentNote />
        ) : dryUnsupported ? (
          <SetupNote>
            {SETUP_PREFIX}
            The live row count for rules you have not saved needs{' '}
            <code>admin_preview_search_guide_rules</code>. Saving still works, and the Venues tab
            shows the real count immediately afterwards.
          </SetupNote>
        ) : (
          <div className="flex flex-wrap items-baseline gap-x-4 gap-y-2">
            <span className="admin-metric text-2xl tabular-nums">{dryBusy ? '…' : (dry?.count ?? '—')}</span>
            <span className="text-[12px] text-muted">
              venues in {cities.find((c) => c.id === (cityId || scopeCityId))?.name ?? 'the preview city'}
            </span>
            {dry && !dryBusy ? (
              dry.renders ? (
                <span className="text-[11px] text-emerald-400">
                  Clears the {MIN_VENUES}-venue minimum, so it renders here.
                </span>
              ) : (
                <span className="text-[11px] text-amber-400">
                  Under {MIN_VENUES} — the app drops it rather than render a short rail.
                </span>
              )
            ) : null}
            {dry?.error ? <span className="text-[11px] text-red-300">{dry.error}</span> : null}
            <span className="ml-auto text-[10px] text-mutedDim">
              Measured by running the real rule against a draft that is rolled back — not a second
              copy of the membership logic.
            </span>
          </div>
        )}
      </section>

      {canEdit ? (
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => void save()} disabled={saving} className="admin-btn admin-btn-primary admin-btn-sm">
            {saving ? 'Saving…' : 'Save rules'}
          </button>
          <span className="text-[11px] text-mutedDim">
            Live / off stays on the row in the table — this saves the rule, not the switch.
          </span>
        </div>
      ) : null}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * CITIES — where this collection is non-empty, and where it actually renders
 * ════════════════════════════════════════════════════════════════════════════════════════════ */

function CitiesTab({ guide }: { guide: GuideRow }) {
  const [coverage, setCoverage] = useState<Coverage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [setupNote, setSetupNote] = useState<string | null>(null);

  const guideId = guide.id;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setSetupNote(null);
    void (async () => {
      const { data, error: rpcErr } = await supabase.rpc('admin_guide_city_coverage', { p_guide_id: guideId });
      if (cancelled) return;
      if (rpcErr) {
        if (rpcMissing(rpcErr.message, 'admin_guide_city_coverage')) {
          setSetupNote(
            SETUP_PREFIX +
              'Per-city coverage is the one thing on this page with no fallback: answering "which ' +
              'cities is this non-empty in" means running the rule once per city, and there is no ' +
              'existing RPC that does it.',
          );
        } else {
          setError(rpcErr.message);
        }
        setCoverage(null);
      } else {
        setCoverage(data as Coverage);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [guideId]);

  const renders = useMemo(() => (coverage?.cities ?? []).filter((c) => c.renders).length, [coverage]);
  const nonEmpty = useMemo(() => (coverage?.cities ?? []).filter((c) => c.n > 0).length, [coverage]);
  const max = useMemo(
    () => Math.max(1, ...(coverage?.cities ?? []).map((c) => c.n)),
    [coverage],
  );

  if (VIEWER_KINDS.has(guide.kind)) return <ViewerDependentNote />;

  return (
    <div className="space-y-4">
      {error ? <ErrorBanner>{error}</ErrorBanner> : null}
      {setupNote && !error ? <SetupNote>{setupNote}</SetupNote> : null}

      {loading ? (
        <SkeletonRows rows={8} />
      ) : !coverage ? null : coverage.scope !== 'per_city' ? (
        <>
          <SetupNote>
            {coverage.scope === 'global'
              ? 'One list worldwide — every reader in every city sees the same venues, so there is no per-city answer to give.'
              : `Fixed to ${coverage.pinned_city_name ?? 'one city'}. Readers everywhere else see this same city’s venues.`}
          </SetupNote>
          <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))' }}>
            <StatCard
              label="Venues"
              value={coverage.cities[0]?.n ?? 0}
              sub={coverage.cities[0]?.renders ? 'Renders' : `Under the ${coverage.min_venues}-venue minimum`}
            />
          </div>
        </>
      ) : (
        <>
          <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))' }}>
            <StatCard
              label="Renders in"
              value={`${renders} of ${coverage.cities.length}`}
              sub={`Cities where it clears ${coverage.min_venues} venues`}
            />
            <StatCard label="Non-empty in" value={nonEmpty} sub="At least one venue, but maybe too few to show" />
            <StatCard
              label="Silent in"
              value={coverage.cities.length - renders}
              sub="Live here and the reader never sees it"
            />
          </div>

          {renders === 0 ? (
            <div className="rounded-md border border-amber-500/30 bg-amber-500/[0.06] px-3 py-2 text-[11px] leading-relaxed text-amber-300">
              This collection renders in <span className="font-semibold">no city at all</span>. If it is
              switched on, it is on for nobody — the app drops any rail it cannot fill to{' '}
              {coverage.min_venues} venues rather than show it short.
            </div>
          ) : null}

          <div className="overflow-hidden rounded-xl border border-border">
            <table className="admin-table">
              <thead>
                <tr>
                  <th className="px-4 py-3 text-left">City</th>
                  <th className="px-4 py-3 text-right">Pool</th>
                  <th className="px-4 py-3 text-right">In this collection</th>
                  <th className="px-4 py-3 text-left">On the page</th>
                </tr>
              </thead>
              <tbody>
                {coverage.cities.map((c, i) => (
                  <tr
                    key={c.city_id ?? c.city_name}
                    className={`border-b border-border/50 ${i % 2 === 0 ? 'bg-surface' : 'bg-rowAlt'}`}
                  >
                    <td className="px-4 py-2.5 align-middle text-sm text-textPrimary">{c.city_name}</td>
                    <td className="px-4 py-2.5 align-middle text-right text-[11px] tabular-nums text-mutedDim">
                      {c.pool ?? '—'}
                    </td>
                    <td className="px-4 py-2.5 align-middle text-right">
                      <div className="flex items-center justify-end gap-2">
                        <div className="h-1.5 w-24 overflow-hidden rounded-full bg-white/[0.06]">
                          <div
                            className={`h-full rounded-full ${c.renders ? 'bg-emerald-500/80' : 'bg-amber-500/70'}`}
                            style={{ width: `${Math.round((c.n / max) * 100)}%` }}
                          />
                        </div>
                        <span className="admin-metric tabular-nums">{c.n}</span>
                      </div>
                    </td>
                    <td className="px-4 py-2.5 align-middle text-[11px]">
                      {c.renders ? (
                        <span className="text-emerald-400">Shows</span>
                      ) : (
                        <span className="text-mutedDim">
                          Dropped — under {coverage.min_venues}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * THE PANEL
 * ════════════════════════════════════════════════════════════════════════════════════════════ */

export function CollectionDetailPanel({
  open,
  guide,
  previewCityId,
  previewCityName,
  cities,
  canEdit = true,
  onClose,
  onGuidePatched,
  onOverridesChanged,
}: {
  open: boolean;
  guide: GuideRow | null;
  previewCityId: string;
  previewCityName: string | null;
  cities: { id: string; name: string }[];
  canEdit?: boolean;
  onClose: () => void;
  onGuidePatched: (id: string, patch: Partial<GuideRow>) => void;
  onOverridesChanged: () => void;
}) {
  const [tab, setTab] = useState<Tab>('venues');
  const [coverOpen, setCoverOpen] = useState(false);

  useEscToClose(open && !coverOpen, onClose);

  /* A different collection is a different question — always land on the venue list. */
  const guideId = guide?.id ?? null;
  useEffect(() => {
    setTab('venues');
    setCoverOpen(false);
  }, [guideId]);

  const patch = useCallback(
    (p: Partial<GuideRow>) => {
      if (guideId) onGuidePatched(guideId, p);
    },
    [guideId, onGuidePatched],
  );

  const onCoverPicked = useCallback(
    (gid: string, venueId: string | null, option: CoverOption | null) => {
      onGuidePatched(gid, {
        cover_venue_id: venueId,
        cover_venue_name: option?.name ?? null,
        cover_photo_url: option?.photo_url ?? null,
        cover_in_scope: venueId != null,
        effective_cover_venue_id: venueId ?? undefined,
        effective_cover_venue_name: venueId ? option?.name ?? null : undefined,
        effective_cover_photo_url: venueId ? option?.photo_url ?? null : undefined,
      });
      setCoverOpen(false);
      onOverridesChanged();
    },
    [onGuidePatched, onOverridesChanged],
  );

  if (!open || !guide) return null;

  const scopeCityId = guide.city_id ?? previewCityId;
  const scopeCityName = guide.city_id ? guide.city_name : previewCityName;
  const viewerScoped = VIEWER_KINDS.has(guide.kind);

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/70 p-4 sm:p-8"
      role="dialog"
      aria-modal="true"
      aria-label={`Edit collection ${guide.title}`}
      onClick={onClose}
    >
      <div className="w-full max-w-5xl rounded-xl border border-border bg-surface" onClick={(e) => e.stopPropagation()}>
        <div className="flex flex-wrap items-start justify-between gap-3 border-b border-border px-5 py-4">
          <div className="min-w-0">
            <h2 className="admin-label-primary text-base">{guide.title}</h2>
            <p className="mt-1 text-xs text-mutedDim">
              {KIND_LABELS[guide.kind] ?? guide.kind} ·{' '}
              {viewerScoped
                ? 'per viewer'
                : guide.city_id
                  ? `fixed to ${guide.city_name ?? 'one city'}`
                  : isCityScoped(guide.kind, guide.city_id)
                    ? `follows the reader — showing ${previewCityName ?? 'no city'}`
                    : 'one list worldwide'}{' '}
              · {guide.is_active ? <span className="text-emerald-400">live</span> : 'off'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-md border border-border px-3 py-1.5 text-xs font-semibold text-muted transition-colors hover:bg-white/[0.04] hover:text-white"
          >
            Close
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-2 border-b border-border px-5 py-3">
          {(
            [
              ['venues', 'Venues'],
              ['rules', 'Rules'],
              ['cities', 'Cities'],
              ['cover', 'Cover'],
            ] as const
          ).map(([id, label]) => (
            <button key={id} type="button" className="admin-pill" data-active={tab === id} onClick={() => setTab(id)}>
              {label}
            </button>
          ))}
        </div>

        <div className="max-h-[72vh] overflow-y-auto px-5 py-5">
          {tab === 'venues' ? (
            <VenuesTab
              guide={guide}
              scopeCityId={scopeCityId}
              scopeCityName={scopeCityName}
              canEdit={canEdit}
              onDirty={onOverridesChanged}
            />
          ) : tab === 'rules' ? (
            <RulesTab guide={guide} scopeCityId={scopeCityId} cities={cities} canEdit={canEdit} onSaved={patch} />
          ) : tab === 'cities' ? (
            <CitiesTab guide={guide} />
          ) : (
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="h-24 w-24 shrink-0 overflow-hidden rounded-lg border border-border bg-black">
                  {guide.effective_cover_photo_url ? (
                    <img
                      src={guide.effective_cover_photo_url}
                      alt=""
                      referrerPolicy="no-referrer"
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <div className="flex h-full items-center justify-center text-[11px] text-mutedDim">none</div>
                  )}
                </div>
                <div className="min-w-0 text-xs leading-relaxed text-muted">
                  <div className="truncate text-textPrimary">{guide.effective_cover_venue_name ?? '—'}</div>
                  <div className="mt-0.5 text-mutedDim">
                    {guide.cover_venue_id
                      ? guide.cover_in_scope
                        ? 'Pinned by hand.'
                        : 'Pinned, but not in this city — the automatic pick is what renders here.'
                      : 'Automatic: the first venue whose photo no other collection is already using.'}
                  </div>
                  {canEdit ? (
                    <button
                      type="button"
                      onClick={() => setCoverOpen(true)}
                      className="admin-btn admin-btn-secondary admin-btn-sm mt-2"
                    >
                      Choose a cover
                    </button>
                  ) : null}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <CollectionCoverPickerModal
        open={coverOpen}
        guide={guide}
        previewCityId={previewCityId}
        previewCityName={previewCityName}
        onClose={() => setCoverOpen(false)}
        onPicked={onCoverPicked}
      />
    </div>
  );
}
